import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import { getErrorTypesRequest } from 'redux/reducers/errorCorrections/reducer';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import {
	getSwedBankIntegrationRequest,
	markAsProcessedRequest,
	uploadStatementFileRequest,
} from 'redux/reducers/swedBank/reducer';
import styled from '@emotion/styled';
import { CloudUpload } from '@mui/icons-material';
import {
	getSwedBankIntegration,
	getSwedBankIntegrationLoad,
} from 'redux/reducers/swedBank/selectors';
import { TFilters, TOrderBy } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { ISwedBankIntegrationItem } from 'redux/reducers/swedBank/types';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { TPageResponse } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { TypeOfDialog } from './types';
import { swedBankStructure } from './swed-bank-structure';
import { MarkAsProcessedDialog } from './MarkAsProcessedDialog';
import { AttachVatReimbursementDialog } from './AttachVatReimbursementDialog';

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

const defaultTableStructure = swedBankStructure as TTableStructure<
	keyof ISwedBankIntegrationItem,
	ISwedBankIntegrationItem
>[];

export const SwedBankTable: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const items = useSelector(getSwedBankIntegration);
	const itemsIsLoading = useSelector(getSwedBankIntegrationLoad);

	const handleTableStructureChange = useCallback(
		(
			newTableStructure: TTableStructure<
				keyof ISwedBankIntegrationItem,
				ISwedBankIntegrationItem
			>[],
		) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	useEffect(() => {
		dispatch(getErrorTypesRequest());
	}, [dispatch]);

	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;

	const swedBankIntegrationRequest = {
		pageNumber: page ? +page : 1,
		pageSize: PAGE_SIZE,
		filters: getFiltersFromNavigateParams(currentTableStructure, navigateParams) as TFilters,
		searchString: String(navigateParams?.search ?? ''),
	};

	// EasTableWithFilter expects a paginated response shape; the API only returns data/totalCount.
	const tableItems: TPageResponse<ISwedBankIntegrationItem> | null = items
		? { ...items, pageNumber: swedBankIntegrationRequest.pageNumber, pageSize: PAGE_SIZE }
		: null;

	useEffect(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+navigateParams.sortIndex].selector as string,
				direction:
					String(navigateParams?.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getSwedBankIntegrationRequest({
				...swedBankIntegrationRequest,
				orderBy,
			}),
		);
		dispatch(getEMerchantRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleUploadStatementFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formData = new FormData();
		formData.append('StatementFile', e.target.files?.[0] as File);
		dispatch(uploadStatementFileRequest(formData));
	};

	const [openMarkAsProcessedDialog, setOpenMarkAsProcessedDialog] = useState(false);
	const setCloseMarkAsProcessedDialog = () => {
		setOpenMarkAsProcessedDialog(false);
	};

	const [openAttachVatReimbursementDialog, setOpenAttachVatReimbursementDialog] = useState(false);
	const setCloseAttachVatReimbursementDialog = () => {
		setOpenAttachVatReimbursementDialog(false);
	};

	const [incomingPaymentId, setIncomingPaymentId] = useState<number | null>(null);

	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false);
	};

	const handleClickMarkAsPaid = () => {
		if (!incomingPaymentId) return;
		dispatch(
			markAsProcessedRequest({
				body: {
					incomingPaymentId: incomingPaymentId ?? 0,
					type: 'skipped',
					invoiceNumber: null,
				},
				swedBankIntegrationRequest,
			}),
		);

		setConfirmationDialogOpen(false);
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		if (typeOfDialog === TypeOfDialog.MARK_AS_PROCESSED) {
			setOpenMarkAsProcessedDialog(true);
			setIncomingPaymentId(id);
		} else if (typeOfDialog === TypeOfDialog.NOT_AN_INVOICE_PAYMENT) {
			setConfirmationDialogOpen(true);
			setIncomingPaymentId(id);
		} else if (typeOfDialog === TypeOfDialog.ATTACH_VAT_REIMBURSEMENT) {
			setOpenAttachVatReimbursementDialog(true);
			setIncomingPaymentId(id);
		}
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<ISwedBankIntegrationItem>
				tableStructure={currentTableStructure}
				items={tableItems}
				itemsIsLoading={itemsIsLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Swed Bank Integration
					</Typography>
					<Button
						component="label"
						variant="contained"
						role={undefined}
						sx={{
							textTransform: 'none',
							height: '24px',
							p: '4px 10px',
							border: '0.5px solid #016193!important',
							fontSize: '0.7rem',
						}}
						size="small"
						tabIndex={-1}
						startIcon={<CloudUpload />}
					>
						Upload Bank Statement
						<VisuallyHiddenInput type="file" onChange={handleUploadStatementFile} multiple />
					</Button>
				</Box>
			</EasTableWithFilter>
			<MarkAsProcessedDialog
				open={openMarkAsProcessedDialog}
				setClose={setCloseMarkAsProcessedDialog}
				data={items?.data?.find((item) => item.id === incomingPaymentId) ?? null}
				incomingPaymentId={incomingPaymentId ?? 0}
				swedBankIntegrationRequest={swedBankIntegrationRequest}
			/>
			<AttachVatReimbursementDialog
				open={openAttachVatReimbursementDialog}
				setClose={setCloseAttachVatReimbursementDialog}
				data={items?.data?.find((item) => item.id === incomingPaymentId) ?? null}
				incomingPaymentId={incomingPaymentId ?? 0}
				swedBankIntegrationRequest={swedBankIntegrationRequest}
			/>
			<ConfirmationDialog
				open={confirmationDialogOpen}
				setClose={handleConfirmationDialogClose}
				handleConfirm={handleClickMarkAsPaid}
				title="Are you sure you want to mark this as<br/><b>Not an Invoice Payment</b>?"
			/>
		</Box>
	);
};
